import { ref } from 'vue'
export function useChat() {
  const idComment = ref('')
  const sendMessage = ref(false)
  const stopScroll = ref(false)
  const getData = ref<boolean>(false)
  const reconnecting = ref(false)
  let reconnectHandler: EventListener | null = null

  const scrollMessage = async (scroller: HTMLElement, scrollTop: number) => {
    if (!idComment.value || stopScroll.value) {
      sendMessage.value = false
      scroller.scrollTop = scrollTop
      return
    }

    const element = document.getElementById(idComment.value)
    if (!element) return

    stopScroll.value = true
    const elementPosition = element.offsetTop - scroller.offsetTop
    scroller.scrollTo({ top: elementPosition, behavior: 'smooth' }) // scroll to chat message in chat area
    setTimeoutScroll(element) // viewport scroll to chat message
  }

  const setTimeoutScroll = (element: HTMLElement, time: number = 1000) => {
    setTimeout(() => {
      const header = document.querySelector('header') as HTMLElement
      const headerHeight = header.offsetHeight
      const padding = 10 // distance expected to header

      if (element) {
        const rect = element.getBoundingClientRect()

        // rect.top: distance from element to top edge of viewport (not relative to document).
        // want to scroll so that the top of the element is below the header (header padding pixels).
        const targetY = rect.top - headerHeight - padding

        window.scrollTo({
          top: targetY,
          behavior: 'smooth',
        })
      }
    }, time)
  }

  const handleReconnect = async (connection, reconnect) => {
    // Skip if the tab is inactive or there is no network connection.
    if (document.visibilityState !== 'visible' || !navigator.onLine) return

    // Skip if the page has not finished loading data for the first time.
    if (!getData.value) return

    // Skip if switching tabs normally while the socket connection remains active (OPEN)
    if (connection.value?.readyState === WebSocket.OPEN) return

    // Skip if currently reconnecting
    if (reconnecting.value) return
    reconnecting.value = true

    try {
      // Only handles the task of re-activating the socket
      // The fetch room data function will be AUTOMATICALLY called by the onConnected callback as soon as the socket connects!
      await reconnect()
    } catch (error) {
      console.error('Failed to handleReconnect', error)
    } finally {
      reconnecting.value = false
    }
  }

  const addReconnectListeners = (connection, reconnect) => {
    removeReconnectListeners()

    reconnectHandler = () => {
      handleReconnect(connection, reconnect)
    }

    // Reconnect after page restoration from bfcache (especially Safari/iOS)
    window.addEventListener('pageshow', reconnectHandler)
    // Reconnect and sync data when user switches back to this tab
    document.addEventListener('visibilitychange', reconnectHandler)
  }

  const removeReconnectListeners = () => {
    if (reconnectHandler) {
      window.removeEventListener('pageshow', reconnectHandler)
      document.removeEventListener('visibilitychange', reconnectHandler)
      reconnectHandler = null
    }
  }

  return {
    idComment,
    sendMessage,
    stopScroll,
    scrollMessage,
    setTimeoutScroll,
    getData,
    addReconnectListeners,
    removeReconnectListeners,
  }
}
